import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { 
  Mail, 
  Phone, 
  MapPin, 
  Instagram, 
  Home,
  ArrowRight,
  Wrench
} from 'lucide-react';

const TikTokIcon = ({ size = 18 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path d="M16.6 5.82a4.28 4.28 0 0 1-1.01-2.82h-3.09v12.4a2.59 2.59 0 1 1-2.59-2.59c.27 0 .53.04.78.12V9.79a5.67 5.67 0 0 0-.78-.05 5.68 5.68 0 1 0 5.68 5.68V9.01a7.35 7.35 0 0 0 4.29 1.37V7.29a4.28 4.28 0 0 1-3.29-1.47z" />
  </svg>
);

const Footer = () => {
  const quickLinks = [
    { name: 'Inicio', href: '#inicio', isLink: false },
    { name: 'Servicios', href: '#servicios', isLink: false },
    { name: 'Paquetes', href: '#paquetes', isLink: false },
    { name: 'Tienda de Herramientas', href: '/tienda', isLink: true },
    { name: 'Solicitar Diseño', href: '#solicitar', isLink: false },
    { name: 'Contacto', href: '#contacto', isLink: false }
  ];

  const services = [
    { name: 'Diseño de Planos Online', href: '#servicios', isLink: false, icon: Home },
    { name: 'Asesoramiento Online', href: '#servicios', isLink: false, icon: Home },
    { name: 'Tienda de Herramientas', href: '/tienda', isLink: true, icon: Wrench },
    { name: 'Diseño Online Premium', href: '#paquetes', isLink: false, icon: Home }
  ];

  const socialLinks = [
    { name: 'Instagram', icon: Instagram, href: 'https://www.instagram.com/drywall_tools_pladur' },
    { name: 'TikTok', icon: TikTokIcon, href: 'https://www.tiktok.com/@drywall_tools_pladur' }
  ];

  return (
    <footer id="contacto" className="bg-slate-900 text-white">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="space-y-6">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <h3 className="text-2xl industrial-logo-footer">DRYWALL</h3>
                <span
                  className="bg-red-600 text-white text-sm font-extrabold tracking-[0.2em] px-2 py-0.5 rounded-md"
                  style={{ fontFamily: "'Orbitron', monospace" }}
                >
                  TOOLS
                </span>
              </div>
              <span className="logo-sub block text-[10px] text-slate-400 mb-4">Drywall Desing LLC</span>
              <p className="text-slate-300 leading-relaxed">
                Especialistas en diseño de planos y asesoramiento técnico online para proyectos de drywall.
                Tu visión, nuestra experiencia, 100% remoto.
              </p>
            </div>
            
            <div className="space-y-3">
              <div className="flex items-center gap-3 text-slate-300">
                <MapPin size={18} className="text-red-400 flex-shrink-0" />
                <span>412 W 7th St, Clovis NM 88101</span>
              </div>
              <div className="flex items-center gap-3 text-slate-300">
                <Phone size={18} className="text-red-400 flex-shrink-0" />
                <span>+1 (505) 226-0487</span>
              </div>
              <div className="flex items-center gap-3 text-slate-300">
                <Mail size={18} className="text-red-400 flex-shrink-0" />
                <span>info@drywall-design.com</span>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold mb-6 text-white">Enlaces Rápidos</h4>
            <ul className="space-y-3">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  {link.isLink ? (
                    <Link
                      to={link.href}
                      className="text-slate-300 hover:text-red-400 transition-colors duration-200 flex items-center gap-2 group"
                    >
                      <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform duration-200" />
                      {link.name}
                    </Link>
                  ) : (
                    <a
                      href={link.href}
                      className="text-slate-300 hover:text-red-400 transition-colors duration-200 flex items-center gap-2 group"
                    >
                      <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform duration-200" />
                      {link.name}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-lg font-semibold mb-6 text-white">Servicios</h4>
            <ul className="space-y-3">
              {services.map((service, index) => {
                const IconComponent = service.icon || Home;
                return (
                  <li key={index}>
                    {service.isLink ? (
                      <Link
                        to={service.href}
                        className="text-slate-300 hover:text-red-400 transition-colors duration-200 flex items-center gap-2 group"
                      >
                        <IconComponent size={14} className="group-hover:scale-110 transition-transform duration-200" />
                        {service.name}
                      </Link>
                    ) : (
                      <a
                        href={service.href}
                        className="text-slate-300 hover:text-red-400 transition-colors duration-200 flex items-center gap-2 group"
                      >
                        <IconComponent size={14} className="group-hover:scale-110 transition-transform duration-200" />
                        {service.name}
                      </a>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Newsletter & Social */}
          <div className="space-y-6">
            <div>
              <h4 className="text-lg font-semibold mb-4 text-white">Mantente Conectado</h4>
              <p className="text-slate-300 text-sm mb-4">
                Recibe las últimas tendencias en diseño y ofertas exclusivas.
              </p>
              <div className="space-y-3">
                <input
                  type="email"
                  placeholder="Tu email"
                  className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500 transition-colors duration-200"
                />
                <Button className="w-full bg-red-600 hover:bg-red-700 text-white transition-all duration-300 hover:scale-[1.02]">
                  Suscribirse
                </Button>
              </div>
            </div>

            {/* Social Links */}
            <div>
              <h4 className="text-lg font-semibold mb-4 text-white">Síguenos</h4>
              <div className="flex gap-4">
                {socialLinks.map((social, index) => {
                  const IconComponent = social.icon;
                  return (
                    <a
                      key={index}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={social.name}
                      data-testid={`footer-social-${social.name.toLowerCase()}`}
                      className="w-10 h-10 bg-slate-800 border border-slate-700 rounded-lg flex items-center justify-center text-slate-400 hover:text-red-400 hover:border-red-500 hover:bg-slate-700 transition-all duration-300 hover:scale-110"
                    >
                      <IconComponent size={18} />
                    </a>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-slate-400 text-sm text-center md:text-left">
              © 2024 DRYWALL DESING LLC. Todos los derechos reservados. Diseñado con ❤️ para crear hogares extraordinarios.
            </p>
            <div className="flex gap-6 text-sm">
              <a href="#" className="text-slate-400 hover:text-red-400 transition-colors duration-200">
                Política de Privacidad
              </a>
              <a href="#" className="text-slate-400 hover:text-red-400 transition-colors duration-200">
                Términos de Servicio
              </a>
              <a href="#" className="text-slate-400 hover:text-red-400 transition-colors duration-200">
                Cookies
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
